import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import Seo, { localBusinessSchema, breadcrumbSchema } from "../components/Seo";
import { Reveal, Overline } from "../components/Reveal";
import CtaBand from "../components/CtaBand";
import { useAreas, useSettings } from "../lib/hooks";

const GROUP_ORDER = ["Bristol", "South Gloucestershire", "Bath & Around", "North Somerset", "Somerset", "South Wales", "Devon"];

export default function Areas() {
  const { data: areas } = useAreas();
  const { data: settings } = useSettings();

  return (
    <div data-testid="areas-page">
      <Seo
        title="Areas We Cover | NOOR Design & Build"
        description="Premium extensions, renovations and conversions across Bristol, Bath, Somerset, South Wales and Devon. Fully insured, 10-year workmanship guarantee."
        path="/areas-we-cover"
        schema={{ "@context": "https://schema.org", "@graph": [localBusinessSchema(settings), breadcrumbSchema([{ name: "Home", path: "/" }, { name: "Areas We Cover", path: "/areas-we-cover" }])] }}
      />
      <section className="bg-graphite text-white blueprint-grid">
        <div className="max-w-[1440px] mx-auto px-5 md:px-10 pt-32 md:pt-44 pb-16 md:pb-24">
          <Overline light>[ Areas We Cover ]</Overline>
          <h1 className="font-display font-light text-4xl sm:text-5xl lg:text-6xl tracking-tighter mt-6 max-w-3xl leading-[1.05]">
            A defined area, served properly.
          </h1>
          <p className="text-white/60 text-base md:text-lg leading-relaxed mt-8 max-w-2xl">
            We concentrate on {settings?.primary_area || "Bristol, Bath, Somerset and the surrounding areas"} so every project
            gets the attention it deserves. We are based in the region and work from our network of trusted local
            trades — we do not claim an office in every town. Larger projects outside the standard area may be
            considered following an initial consultation.
          </p>
        </div>
      </section>

      <section className="bg-cream">
        <div className="max-w-[1440px] mx-auto px-5 md:px-10 py-16 md:py-28 space-y-20">
          {GROUP_ORDER.map((group) => {
            const list = (areas || []).filter((a) => (a.group || "Bristol") === group);
            if (!list.length) return null;
            return (
              <div key={group} data-testid={`area-group-${group.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                <div className="flex items-baseline justify-between gap-6 border-b border-black/15 pb-5">
                  <h2 className="font-display font-light text-3xl md:text-4xl tracking-tight text-graphite">{group}</h2>
                  <p className="font-mono text-[10px] tracking-[0.25em] uppercase text-graphite/40 shrink-0">
                    {list.length} {list.length === 1 ? "area" : "areas"}
                  </p>
                </div>
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-12 mt-10">
                  {list.map((a, i) => (
                    <Reveal key={a.slug} delay={(i % 3) * 0.06}>
                      <Link
                        to={`/areas-we-cover/${a.slug}`}
                        data-testid={`area-card-${a.slug}`}
                        className="group block"
                      >
                        {a.image && (
                          <div className="overflow-hidden aspect-[4/3] bg-stone">
                            <img
                              src={a.image}
                              alt={a.name}
                              loading="lazy"
                              className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.05]"
                            />
                          </div>
                        )}
                        <div className="flex items-start justify-between gap-4 mt-5">
                          <div>
                            <p className="font-mono text-[10px] tracking-[0.25em] uppercase text-ember">{a.eyebrow}</p>
                            <h3 className="font-display font-light text-2xl tracking-tight mt-2 text-graphite group-hover:text-ember transition-colors duration-300">
                              {a.name}
                            </h3>
                          </div>
                          <ArrowUpRight
                            size={18}
                            strokeWidth={1.5}
                            className="mt-2 shrink-0 text-graphite/30 group-hover:text-ember transition-all duration-300 group-hover:translate-x-1 group-hover:-translate-y-1"
                          />
                        </div>
                      </Link>
                    </Reveal>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </section>
      <CtaBand />
    </div>
  );
}
